import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from '@/i18n/useTranslation';

interface DeleteConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  message?: string;
  isDeleting?: boolean;
}

export default function DeleteConfirmModal({ 
  isOpen, 
  onClose, 
  onConfirm,
  title,
  message,
  isDeleting = false
}: DeleteConfirmModalProps) {
  const { t } = useTranslation();
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-bg-panel rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        <div className="p-6">
          {/* Icon */}
          <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
            <AlertTriangle className="w-6 h-6 text-red-500" strokeWidth={2} />
          </div>

          {/* Title */}
          <h2 className="text-xl font-semibold text-text-primary text-center mb-2">
            {title || t('delete.title')}
          </h2>

          {/* Message */}
          <p className="text-text-secondary text-center mb-6">
            {message || t('delete.confirm')}
          </p>

          {/* Buttons */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={isDeleting}
              className="flex-1 px-4 py-2.5 bg-bg-control text-text-primary rounded-full text-sm font-medium hover:bg-bg-control-hover transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('button.cancel')}
            </button>
            <button
              onClick={() => {
                onConfirm();
              }}
              disabled={isDeleting}
              className="flex-1 px-4 py-2.5 bg-red-500 text-white rounded-full text-sm font-semibold hover:bg-red-600 transition-all disabled:opacity-80 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isDeleting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>{t('delete.deleting')}</span>
                </>
              ) : (
                t('button.delete')
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

